import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

import { Gate } from '../../../global/contexts/auth/auth';
import { cleanName, orNull } from '../../../common/utils/text';
import { useConfirm } from '../../../common/components/ConfirmDialog/confirm';
import { useTemplatesList, useUpdateTemplate, useTestSendTemplate } from '../contexts/notifications';
import { templateItems, isArchived, DEFAULT_CHANNEL } from '../utils/format';
import { validateTemplate } from '../utils/validate';
import TemplateFields from './fields';

import '../../../common/styles/catalog.css';

function buildForm(t) {
    return {
        code:         t.code || '',
        name:         t.name || '',
        channel:      t.channel || DEFAULT_CHANNEL,
        trigger:      t.trigger || '',
        provider_sid: t.provider_sid || '',
        parameters:   Array.isArray(t.parameters) ? t.parameters : [],
    };
}

// Envío de prueba (POST :code/test-send). Se apoya en los parameters de la plantilla.
function TestSend({ code, parameters }) {
    const { mutateAsync, isPending } = useTestSendTemplate(code);
    const [to, setTo] = useState('');
    const [vars, setVars] = useState({});

    const setVar = (key, value) => setVars(prev => ({ ...prev, [key]: value }));

    const handleSend = async () => {
        if (!to.trim()) { toast.error('Indicá un número de destino'); return; }
        try {
            const res = await mutateAsync({ to: to.trim(), variables: vars });
            if (res?.sent === false) toast.error(`No se envió: ${res.reason || 'motivo desconocido'}`);
            else toast.success('Prueba enviada (o encolada por Twilio)');
        } catch (err) {
            if (err?.status === 400) toast.error('Número de destino inválido');
            else toast.error(err?.message || 'No se pudo enviar la prueba');
        }
    };

    return (
        <section className="cat-form__grid ntf-test">
            <div className="cat-form__field cat-form__field--full">
                <h2 className="cat-form__section-title">Probar envío</h2>
                <span className="cat-form__hint">
                    Envía la plantilla a un número. Sin Twilio configurado, el backend lo registra por log.
                </span>
            </div>

            <label className="cat-form__field">
                <span className="cat-form__label">Destino</span>
                <input
                    type="tel"
                    value={to}
                    onChange={(e) => setTo(e.target.value)}
                    placeholder="+57 3XX XXX XXXX"
                    disabled={isPending}
                />
            </label>

            {parameters.map((p) => (
                <label key={p} className="cat-form__field">
                    <span className="cat-form__label">{p}</span>
                    <input
                        type="text"
                        value={vars[p] || ''}
                        onChange={(e) => setVar(p, e.target.value)}
                        disabled={isPending}
                    />
                </label>
            ))}

            <div className="cat-form__field cat-form__field--full ntf-test__actions">
                <button type="button" className="cat-detail__edit" onClick={handleSend} disabled={isPending}>
                    {isPending ? 'Enviando…' : 'Enviar prueba'}
                </button>
            </div>
        </section>
    );
}

export default function EditTemplateForm({ code }) {
    const navigate = useNavigate();
    // No hay GET por :code — resolvemos la plantilla desde la lista (misma caché que el overview).
    const { data, isLoading, error: loadError } = useTemplatesList();
    const template = useMemo(() => templateItems(data).find((t) => String(t.code) === String(code)), [data, code]);

    const { mutateAsync: updateTemplate, isPending: updating } = useUpdateTemplate(code);
    const confirm = useConfirm();

    const [form, setForm]           = useState(null);
    const [seededFor, setSeededFor] = useState(null);
    const [localErrors, setLocalErrors]   = useState({});
    const [serverFields, setServerFields] = useState({});

    if (template && seededFor !== template.code) {
        setSeededFor(template.code);
        setForm(buildForm(template));
    }

    const setField = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
        if (localErrors[field] || serverFields[field]) {
            setLocalErrors(prev => ({ ...prev, [field]: undefined }));
            setServerFields(prev => ({ ...prev, [field]: undefined }));
        }
    };
    const fieldError = (field) => localErrors[field] || serverFields[field];

    if (isLoading) return <div className="cat-form__state">Cargando plantilla…</div>;
    if (loadError) return <div className="cat-form__state">No se pudo cargar la plantilla.</div>;
    if (!template) return <div className="cat-form__state">Plantilla no encontrada.</div>;
    if (!form) return <div className="cat-form__state">Cargando plantilla…</div>;

    const archived = isArchived(template);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errors = validateTemplate(form, { isEdit: true });
        if (Object.keys(errors).length) { setLocalErrors(errors); return; }
        setLocalErrors({});
        setServerFields({});

        try {
            // PATCH solo campos editables (contrato): name/provider_sid/parameters/trigger.
            await updateTemplate({
                name:         cleanName(form.name),
                trigger:      orNull(form.trigger),
                provider_sid: orNull(form.provider_sid),
                parameters:   form.parameters,
            });
            toast.success('Plantilla actualizada');
            navigate('/admin/notifications');
        } catch (err) {
            if (err?.fields) { setServerFields(err.fields); toast.error('Revisá los campos marcados'); }
            else toast.error(err?.message || 'No se pudo actualizar la plantilla');
        }
    };

    const handleArchive = async () => {
        const ok = await confirm({
            title: 'Archivar plantilla',
            message: `¿Archivar la plantilla "${template.name}"? Dejará de enviarse.`,
            confirmLabel: 'Archivar',
            danger: true,
        });
        if (!ok) return;
        try {
            await updateTemplate({ status: 0 });
            toast.success('Plantilla archivada');
            navigate('/admin/notifications');
        } catch (err) {
            toast.error(err?.message || 'No se pudo archivar la plantilla');
        }
    };

    return (
        <div className="cat-form">
            <header className="cat-form__header">
                <Link to="/admin/notifications" className="cat-form__back">Notificaciones</Link>
                <h1 className="cat-form__title">Editar plantilla</h1>
                <span className="cat-form__subtitle">
                    <span className="ntf-code">{template.code}</span>
                    {archived && <span className="cat-badge cat-badge--0 ntf-subtitle-badge">Archivada</span>}
                </span>
            </header>

            <form onSubmit={handleSubmit} className="ntf-form-block">
                <TemplateFields form={form} setField={setField} fieldError={fieldError} disabled={updating} isEdit />

                <footer className="cat-form__footer">
                    <Link to="/admin/notifications" className="cat-form__cancel">Cancelar</Link>
                    {!archived && (
                        <Gate domain="notifications" action="write">
                            <button type="button" className="cat-form__danger" onClick={handleArchive} disabled={updating}>
                                Archivar
                            </button>
                        </Gate>
                    )}
                    <button type="submit" className="cat-form__submit" disabled={updating || !form.name.trim()}>
                        {updating ? 'Guardando…' : 'Guardar cambios'}
                    </button>
                </footer>
            </form>

            <Gate domain="notifications" action="write">
                <TestSend code={template.code} parameters={form.parameters} />
            </Gate>
        </div>
    );
}
